import { useState } from "react";
import { ShieldCheck, RefreshCw, Trash2, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useAzure } from "@/context/AzureContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

export default function RbacView() {
  const { state, run, refresh } = useAzure();
  const [open, setOpen] = useState(false);
  const users = state?.entra_users || [];
  const groups = state?.entra_groups || [];
  const subs = state?.subscriptions || [];
  const rgs = state?.resource_groups || [];
  const roles = state?.roles || [];
  const ras = state?.role_assignments || [];

  const [form, setForm] = useState({ principal_id: "", role: "Reader", scope: "" });

  const scopeOptions = [
    ...subs.map((s) => ({ label: `Subscription: ${s.name}`, value: `/subscriptions/${s.id}` })),
    ...rgs.map((r) => ({ label: `Resource group: ${r.name}`, value: r.id })),
  ];

  const scopeLabel = (scope) => scopeOptions.find((o) => o.value === scope)?.label || scope;

  const submit = () =>
    run(() => api.post("/azure/role-assignments", form), { success: "Role assignment added" }).then((r) => { if (r.ok) setOpen(false); });

  const del = (ra) => run(() => api.delete(`/azure/role-assignments/${ra.id}`), { success: "Role assignment removed" });

  return (
    <div data-testid="az-rbac">
      <div className="flex items-center gap-2 border-b border-border pb-3 mb-3">
        <Button size="sm" onClick={() => { setForm({ principal_id: "", role: "Reader", scope: scopeOptions[0]?.value || "" }); setOpen(true); }} data-testid="az-add-ra-btn" className="bg-[#0078d4] hover:bg-[#106ebe] text-white gap-2"><Plus className="h-4 w-4" /> Add role assignment</Button>
        <Button size="sm" variant="ghost" onClick={refresh} className="gap-2"><RefreshCw className="h-4 w-4" /> Refresh</Button>
      </div>

      <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-4 py-2.5 mb-3 text-xs text-amber-700 dark:text-amber-400 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4" /> Azure RBAC (resource access) is separate from Microsoft Entra administrative roles (directory management).
      </div>

      <div className="border border-border rounded-md overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Principal</TableHead><TableHead>Type</TableHead><TableHead>Role</TableHead><TableHead>Scope</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {ras.map((ra) => (
              <TableRow key={ra.id} data-testid={`az-ra-row-${ra.role}`}>
                <TableCell className="font-medium">{ra.principal_name}</TableCell>
                <TableCell>{ra.principal_type}</TableCell>
                <TableCell>{ra.role}</TableCell>
                <TableCell className="text-muted-foreground text-xs">{scopeLabel(ra.scope)}</TableCell>
                <TableCell><Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => del(ra)}><Trash2 className="h-4 w-4" /></Button></TableCell>
              </TableRow>
            ))}
            {ras.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No role assignments.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add role assignment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Role *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="az-ra-role"><SelectValue /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Assign to (member) *</Label>
              <Select value={form.principal_id} onValueChange={(v) => setForm({ ...form, principal_id: v })}>
                <SelectTrigger data-testid="az-ra-principal"><SelectValue placeholder="Select user or group" /></SelectTrigger>
                <SelectContent>
                  {groups.map((g) => <SelectItem key={g.object_id} value={g.object_id}>Group · {g.name}</SelectItem>)}
                  {users.map((u) => <SelectItem key={u.object_id} value={u.object_id}>User · {u.display_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Scope *</Label>
              <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                <SelectTrigger data-testid="az-ra-scope"><SelectValue placeholder="Select scope" /></SelectTrigger>
                <SelectContent>{scopeOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="az-ra-submit" onClick={submit} className="bg-[#0078d4] hover:bg-[#106ebe] text-white" disabled={!form.principal_id || !form.scope}>Assign</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
